import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { ChevronDown, LogOut, Printer, Search, Users } from "lucide-react";
import { API_URL } from "../data/content";
import { listForGender, genderLabel } from "../data/measurements";
import { Tag, SolidButton } from "../components/Shared";

const api = axios.create({ baseURL: API_URL, withCredentials: true });

const inputCls =
  "w-full border border-line/15 bg-bone/5 px-4 py-3.5 text-sm text-bone placeholder:text-fog/60 focus:border-flame focus:outline-none transition-colors duration-300";

const StaffLogin = ({ onAuth }) => {
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/auth/login", form);
      onAuth(data);
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Sign in failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-md border border-line/10 bg-coal p-8 sm:p-10" data-testid="atelier-login-card">
      <p className="font-mono text-[10px] uppercase tracking-[0.35em] text-flame">Staff Only</p>
      <h1 className="mt-3 font-serif text-4xl font-light">The Atelier.</h1>
      <form onSubmit={submit} className="mt-8 space-y-5" data-testid="atelier-login-form">
        <input required type="email" data-testid="atelier-email-input" className={inputCls} placeholder="Studio email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} />
        <input required type="password" data-testid="atelier-password-input" className={inputCls} placeholder="Password" value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} />
        {error && <p className="font-mono text-xs text-flame" data-testid="atelier-login-error">{error}</p>}
        <SolidButton type="submit" testId="atelier-login-submit">{busy ? "Signing in..." : "Sign In"}</SolidButton>
      </form>
    </div>
  );
};

const MemberFile = ({ member, unit }) => {
  const list = listForGender(member.gender);
  const values = member.values || {};
  return (
    <div className="border border-line/10 bg-ink p-6" data-testid={`member-file-${member.label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="font-serif text-2xl font-normal">{member.label} <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-flame">{genderLabel(member.gender)}</span></p>
        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-fog">
          {member.updated_at ? `Updated ${new Date(member.updated_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}` : "No entries yet"}
        </p>
      </div>
      <div className="mt-5 grid grid-cols-2 gap-x-8 gap-y-2.5 sm:grid-cols-3">
        {list.map((it) => (
          <div key={it.key} className="flex items-baseline justify-between border-b border-line/10 pb-1.5" data-testid={`file-row-${it.key}`}>
            <span className="text-xs font-light text-fog">{it.label}</span>
            <span className={`font-mono text-xs ${values[it.key] ? "text-flame" : "text-fog/50"}`}>
              {values[it.key] ? `${values[it.key]} ${unit}` : "—"}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

const Dashboard = ({ user, onLogout }) => {
  const [clients, setClients] = useState(null);
  const [query, setQuery] = useState("");
  const [openId, setOpenId] = useState(null);
  const [files, setFiles] = useState({});

  useEffect(() => {
    api.get("/atelier/clients").then(({ data }) => setClients(data.clients)).catch(() => setClients([]));
  }, []);

  const filtered = useMemo(() => {
    if (!clients) return [];
    const q = query.trim().toLowerCase();
    if (!q) return clients;
    return clients.filter((c) => c.name.toLowerCase().includes(q) || c.email.toLowerCase().includes(q));
  }, [clients, query]);

  const totals = useMemo(() => {
    if (!clients) return { members: 0, entries: 0 };
    return {
      members: clients.reduce((a, c) => a + c.members.length, 0),
      entries: clients.reduce((a, c) => a + c.members.reduce((x, m) => x + m.done, 0), 0),
    };
  }, [clients]);

  const toggleFile = async (c) => {
    const id = c.id;
    setOpenId((o) => (o === id ? null : id));
    if (!files[id]) {
      const { data } = await api.get(`/atelier/clients/${id}`);
      setFiles((f) => ({ ...f, [id]: data.members }));
    }
  };

  return (
    <div data-testid="atelier-dashboard">
      <div className="flex flex-wrap items-end justify-between gap-8">
        <div>
          <Tag>The Atelier — Staff</Tag>
          <h1 className="mt-6 font-serif text-5xl font-light tracking-tight sm:text-6xl">CLIENT <span className="text-flame">FILES.</span></h1>
          <p className="mt-4 text-base font-light text-fog">Signed in as {user.name} — every registered client and their measurement files.</p>
        </div>
        <button type="button" onClick={onLogout} data-testid="atelier-logout-button" className="flex items-center gap-2 border border-line/15 px-5 py-3 font-mono text-[10px] uppercase tracking-[0.25em] text-fog transition-colors hover:border-flame hover:text-flame">
          <LogOut className="h-3.5 w-3.5" /> Logout
        </button>
      </div>

      <div className="mt-10 grid grid-cols-3 gap-px border border-line/10 bg-line/10" data-testid="atelier-stats">
        {[["Registered Clients", clients?.length ?? "—"], ["Measurement Profiles", totals.members], ["Measurements on File", totals.entries]].map(([l, v]) => (
          <div key={l} className="bg-coal p-6 text-center">
            <p className="font-serif text-4xl font-light text-flame">{v}</p>
            <p className="mt-2 font-mono text-[9px] uppercase tracking-[0.25em] text-fog">{l}</p>
          </div>
        ))}
      </div>

      <div className="relative mt-10 max-w-md">
        <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-fog" />
        <input data-testid="atelier-search-input" className={`${inputCls} pl-11`} placeholder="Search by name or email" value={query} onChange={(e) => setQuery(e.target.value)} />
      </div>

      <div className="mt-8 space-y-4">
        {clients === null && <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-fog">Loading client files…</p>}
        {clients !== null && filtered.length === 0 && <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-fog" data-testid="atelier-empty">No clients found.</p>}
        {filtered.map((c) => {
          const unit = c.profile?.units === "cm" ? "cm" : "in";
          const open = openId === c.id;
          return (
            <div key={c.id} className="border border-line/10 bg-coal" data-testid={`client-card-${c.email}`}>
              <button type="button" onClick={() => toggleFile(c)} className="flex w-full flex-wrap items-center justify-between gap-4 p-6 text-left" data-testid={`client-toggle-${c.email}`}>
                <div>
                  <p className="font-serif text-2xl font-normal">{c.name}</p>
                  <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-fog">{c.email}{c.phone ? ` · ${c.phone}` : ""}</p>
                </div>
                <div className="flex items-center gap-4">
                  <div className="flex flex-wrap gap-2">
                    {c.members.map((m) => (
                      <span key={m.id} className="border border-line/15 px-3 py-1.5 font-mono text-[9px] uppercase tracking-[0.15em] text-fog">
                        {m.label} · {genderLabel(m.gender)} · <span className={m.done ? "text-flame" : ""}>{m.done} rec.</span>
                      </span>
                    ))}
                  </div>
                  <ChevronDown className={`h-4 w-4 text-flame transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
                </div>
              </button>
              {open && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4 border-t border-line/10 p-6" data-testid={`client-file-${c.email}`}>
                  <div className="flex items-center justify-between">
                    <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-fog">
                      {c.profile ? `${c.profile.fit} fit${c.profile.height ? ` · Height ${c.profile.height} ${unit}` : ""} · units: ${unit}` : "Profile incomplete"}
                    </p>
                    <button type="button" onClick={() => window.print()} data-testid={`client-print-${c.email}`} className="flex items-center gap-2 border border-flame px-4 py-2 font-mono text-[9px] uppercase tracking-[0.2em] text-flame transition-all hover:bg-flame hover:text-ink">
                      <Printer className="h-3.5 w-3.5" /> Print File
                    </button>
                  </div>
                  {(files[c.id] || []).map((m) => <MemberFile key={m.id} member={m} unit={unit} />)}
                  {files[c.id] && files[c.id].length === 0 && <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-fog">No measurement profiles yet.</p>}
                </motion.div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default function AtelierPage() {
  const [user, setUser] = useState(null);

  useEffect(() => {
    api.get("/auth/me").then(({ data }) => setUser(data)).catch(() => setUser(false));
  }, []);

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    setUser(false);
  };

  return (
    <div className="min-h-screen bg-ink px-5 pb-32 pt-32 sm:px-10 md:pt-40" data-testid="atelier-page">
      <div className="mx-auto max-w-[1400px]">
        {user === null && (
          <div className="flex min-h-[40vh] items-center justify-center">
            <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-fog">Opening the atelier…</span>
          </div>
        )}
        {user === false && <StaffLogin onAuth={setUser} />}
        {user && user.role !== "admin" && (
          <div className="mx-auto max-w-md border border-line/10 bg-coal p-10 text-center" data-testid="atelier-denied">
            <Users className="mx-auto h-6 w-6 text-flame" />
            <h1 className="mt-4 font-serif text-3xl font-light">Staff access only.</h1>
            <p className="mt-3 text-sm font-light text-fog">This area is reserved for the Rutu Bhonsle atelier. Your measurement file lives under Measure.</p>
            <button type="button" onClick={logout} data-testid="atelier-denied-logout" className="mt-6 font-mono text-[10px] uppercase tracking-[0.25em] text-flame">Sign out →</button>
          </div>
        )}
        {user && user.role === "admin" && <Dashboard user={user} onLogout={logout} />}
      </div>
    </div>
  );
}
